import { mapPathControls } from '@peruselab/structure-editor/domain/pathEditing';
import { roundCoordinate } from '@peruselab/structure-editor/domain/drawingDocumentCore';
import { applicableStyle, type AnnotationStyle } from '@peruselab/structure-editor/domain/annotationStyle';
import type { PaperShape } from '@peruselab/structure-editor/domain/objectEditing';
import { useCallback } from 'react';
import { unprojectPointPreservingAspect } from '@peruselab/structure-editor/domain/aspectProjection';
import type { NormalizedPoint, TextParagraph, Annotation } from '@peruselab/structure-editor/model';
import { isEmptyText } from '@peruselab/structure-editor/domain/richText';
import type { PaperSize } from '@peruselab/structure-editor/domain/geometry';
import {
  emptySelection,
  type ArmedBondTool,
  type EditorSelection,
  type EditorTool
} from '@peruselab/structure-editor/domain/editor';
import { commitHistory, type DocumentHistory } from '@peruselab/structure-editor/domain/history';
import {
  appendAnnotation,
  appendLineToDocument,
  documentToLines,
  edgeAtLine,
  nodeAtPoint,
  setBondCrossedDouble,
  setBondDative,
  setBondAgostic,
  setBondHydrogen,
  ionizePair,
  deleteBond,
  setBondWavy,
  setBondOrder,
  setBondWedge,
  setPerceivedAromaticity
} from '@peruselab/structure-editor/domain/graphDrawing';
import { aromaticAtomIds, aromaticBondIds } from '@peruselab/structure-editor/domain/documentSelectors';
import type { RingTemplate } from '@peruselab/structure-editor/domain/ringTemplates';
import type { PaperArrow, PaperAnchor } from '@peruselab/structure-editor/domain/annotationRendering';
import { addHaptoBond, haptoLigandAt } from '@peruselab/structure-editor/domain/haptoBonds';
import { ATOM_ATTACH_DISTANCE_PX } from '@peruselab/structure-editor/domain/bonds';

const NO_ATOMS: readonly string[] = [];
import type { DrawingDocument, DrawingLine } from '../generated/drawing-contract';

export interface UseDrawingGesturesOptions {
  readonly annotationStyleDefaults: AnnotationStyle;
  readonly setHistory: (updater: (current: DocumentHistory) => DocumentHistory) => void;
  readonly setSelection: (next: EditorSelection) => void;
  readonly setTool: (tool: EditorTool) => void;
  readonly paperSize: PaperSize;
  readonly isOversizedImport: boolean;
  readonly armedBondTool: ArmedBondTool;
  /** ADR 0026 D2: the selected atoms, a hapto bond's ligand when it is drawn onto one of them. */
  readonly selectedAtomIds?: readonly string[];
}

// UI action rules Rule 3: a bond drawn on the drawingPaper while a bond tool is armed is created
// as that kind. `reverse` has nothing to apply to a fresh plain bond.
function applyArmedBondToolToNewEdge(
  document: DrawingDocument,
  edgeId: string,
  edgeSource: string,
  armedBondTool: ArmedBondTool
): DrawingDocument {
  switch (armedBondTool.kind) {
    case 'order':
      return armedBondTool.order === 1 ? document : setBondOrder(document, edgeId, armedBondTool.order);
    case 'wedge':
      return setBondWedge(document, edgeId, armedBondTool.style);
    case 'crossed-double':
      return setBondCrossedDouble(setBondOrder(document, edgeId, 2), edgeId, true);
    case 'dative':
      return setBondDative(document, edgeId, edgeSource);
    case 'ionic': {
      // ADR 0022 (as amended): no bond is stored -- the two atoms become ions and the paper draws
      // the bolt between them.
      const edge = document.structure.graph.edges.find((candidate) => candidate.id === edgeId);
      return edge ? ionizePair(deleteBond(document, edgeId), edge.source, edge.target) : document;
    }
    case 'hydrogen':
      return setBondHydrogen(document, edgeId);
    case 'agostic':
      return setBondAgostic(document, edgeId);
    case 'wavy':
      return setBondWavy(document, edgeId, edgeSource);
    default:
      return document;
  }
}

// A `point` anchor arrives in surface-normalized space; convert it to depiction coordinates on
// commit (atom / bond anchors resolve later against the graph, so they pass straight through).
// Shared by the curved-arrow and reaction-arrow placement handlers.
function anchorToDepictionSpace(
  anchor: PaperAnchor,
  source: PaperSize,
  paperSize: PaperSize,
  preserveAspect: boolean
): PaperAnchor {
  if (anchor.kind !== 'point') return anchor;
  const coordNorm = unprojectPointPreservingAspect(anchor.at, source, paperSize, preserveAspect, false);
  return { kind: 'point', at: { x: coordNorm.x * source.width, y: coordNorm.y * source.height } };
}

/**
 * Drawing-paper gesture handlers for useDrawingDocument.ts (roadmap item 35): freehand line,
 * ring/chain template, decorative-shape, and curved-arrow placement (ADR 0008 R5.3). Each does its
 * own coordinate unprojection and builds its own `setHistory` producer rather than routing through
 * `commitDocument` -- their no-op-detection quirks (e.g. a rejected duplicate edge still needing a
 * fresh object to bust DrawingPaper's line cache) differ per handler, unlike the generic
 * commit-or-not shape `commitDocument` provides.
 */
export function useDrawingGestures({
  annotationStyleDefaults,
  setHistory,
  setSelection,
  setTool,
  paperSize,
  isOversizedImport,
  armedBondTool,
  selectedAtomIds = NO_ATOMS
}: UseDrawingGesturesOptions) {
  const handleLinesChange = useCallback(
    (nextLines: readonly DrawingLine[]) => {
      setHistory((current) => {
        const currentLines = documentToLines(current.present);
        if (nextLines.length <= currentLines.length) return current;
        const sourceSize = {
          width: current.present.depiction.coordinateSet.width,
          height: current.present.depiction.coordinateSet.height
        };
        const appendedLines = nextLines.slice(currentLines.length).map((line) => ({
          ...line,
          start: unprojectPointPreservingAspect(line.start, sourceSize, paperSize, !isOversizedImport),
          end: unprojectPointPreservingAspect(line.end, sourceSize, paperSize, !isOversizedImport)
        }));
        // ADR 0026 D2: a hapto bond starts on the metal and ends in a ring, on a bond or on one of
        // the selected atoms; no atom is added anywhere, and nothing happens elsewhere.
        if (armedBondTool.kind === 'hapto') {
          const { width, height } = current.present.depiction.coordinateSet;
          const hapto = appendedLines.reduce((drawing, line) => {
            const metal = nodeAtPoint(drawing, line.start);
            if (!metal) return drawing;
            const end = { x: line.end.x * width, y: line.end.y * height };
            const ligand = haptoLigandAt(
              drawing,
              end,
              ATOM_ATTACH_DISTANCE_PX,
              selectedAtomIds.filter((id) => id !== metal.id)
            );
            return addHaptoBond(drawing, metal.id, ligand);
          }, current.present);
          return hapto === current.present
            ? { ...current, present: { ...current.present } }
            : commitHistory(current, hapto);
        }
        let updated = appendedLines.reduce((drawing, line) => appendLineToDocument(drawing, line), current.present);
        // ADR 0022 (as amended): the bolt makes two existing atoms ions; drawn into empty space it
        // would leave a stray charged atom, so it does nothing there.
        if (
          armedBondTool.kind === 'ionic' &&
          appendedLines.some((line) => {
            const edge = edgeAtLine(updated, line);
            return (
              !edge || !current.present.structure.atoms[edge.source] || !current.present.structure.atoms[edge.target]
            );
          })
        )
          return { ...current, present: { ...current.present } };
        if (armedBondTool.kind !== 'reverse') {
          for (const line of appendedLines) {
            const edge = edgeAtLine(updated, line);
            if (edge) updated = applyArmedBondToolToNewEdge(updated, edge.id, edge.source, armedBondTool);
          }
          // ADR 0026 D6: an agostic interaction only from a C–H bond's hydrogen to a metal --
          // anything else drawn with it leaves the paper as it was, rather than a stray bond.
          if (
            armedBondTool.kind === 'agostic' &&
            appendedLines.some(
              (line) => updated.structure.bonds[edgeAtLine(updated, line)?.id ?? '']?.type !== 'agostic'
            )
          )
            return { ...current, present: { ...current.present } };
        }
        const newEdges = appendedLines.flatMap((line) => {
          const edge = edgeAtLine(updated, line);
          return edge ? [edge] : [];
        });
        // The Bonds rail already falls back to the most recently drawn bond (see
        // useSelectionTargets.ts `activeBondId`), so a freshly drawn bond doesn't need to be
        // selected here -- doing so only left unwanted object-editing handles on it.
        if (newEdges.length) setSelection(emptySelection());
        // A new object also resets DrawingPaper's transient line cache when a
        // self-loop or duplicate edge is rejected by the graph model.
        if (updated === current.present) return { ...current, present: { ...current.present } };
        return commitHistory(current, updated);
      });
    },
    [armedBondTool, isOversizedImport, setHistory, setSelection, paperSize, selectedAtomIds]
  );

  const handleRingPlace = useCallback(
    (ringLines: readonly DrawingLine[], template: RingTemplate, sharedBondId?: string) => {
      setHistory((current) => {
        const sourceSize = {
          width: current.present.depiction.coordinateSet.width,
          height: current.present.depiction.coordinateSet.height
        };
        const documentRingLines = ringLines.map((line) => ({
          ...line,
          start: unprojectPointPreservingAspect(line.start, sourceSize, paperSize, !isOversizedImport),
          end: unprojectPointPreservingAspect(line.end, sourceSize, paperSize, !isOversizedImport)
        }));
        // #545: `appendLineToDocument` replaces graph topology under the hood, which
        // unconditionally clears any existing aromaticity/cip/partial-charge (a topology change
        // invalidates whole-molecule perception results -- see replaceTopology in
        // drawingDocumentCore.ts). Read what's already aromatic *before* that happens, so placing
        // a second ring can restore it alongside the new ring's own atoms/bonds instead of losing it.
        const existingAromaticAtomIds = aromaticAtomIds(current.present);
        const existingAromaticBondIds = aromaticBondIds(current.present);
        let updated = documentRingLines.reduce(
          (drawing, candidate) => appendLineToDocument(drawing, candidate),
          current.present
        );
        const ringEdgeIdAt = (index: number, candidate: DrawingLine) =>
          index === 0 && sharedBondId ? sharedBondId : edgeAtLine(updated, candidate)?.id;
        // Aromatic benzene doubles the alternating edges (0, 2, 4); the cycloalkene / cyclodiene
        // templates carry their own edge list. Edge 0 routes through `sharedBondId` when fused.
        const doubleBondEdges = template.aromatic
          ? documentRingLines.map((_, index) => index).filter((index) => index % 2 === 0)
          : (template.doubleBondEdges ?? []);
        for (const index of doubleBondEdges) {
          const candidate = documentRingLines[index];
          if (!candidate) continue;
          const edgeId = ringEdgeIdAt(index, candidate);
          if (edgeId) updated = setBondOrder(updated, edgeId, 2);
        }
        // An aromatic ring template is a real aromaticity perception result, not just a depiction
        // shortcut for alternating bond orders -- record it the same way SMILES import's RDKit
        // perception does, merged with whatever aromaticity already existed before this edit.
        if (template.aromatic) {
          const ringNodeIds = documentRingLines.flatMap((line) =>
            [nodeAtPoint(updated, line.start)?.id, nodeAtPoint(updated, line.end)?.id].filter(
              (id): id is string => id !== undefined
            )
          );
          const ringEdgeIds = documentRingLines.flatMap((line, index) => {
            const edgeId = ringEdgeIdAt(index, line);
            return edgeId ? [edgeId] : [];
          });
          updated = setPerceivedAromaticity(
            updated,
            [...existingAromaticAtomIds, ...ringNodeIds],
            [...existingAromaticBondIds, ...ringEdgeIds]
          );
        }
        // Same as a freehand bond (see handleLinesChange above): the placed ring doesn't need to
        // stay selected, and leaving it selected only left unwanted object-editing handles on it.
        setSelection(emptySelection());
        return updated === current.present ? current : commitHistory(current, updated);
      });
    },
    [isOversizedImport, setHistory, setSelection, paperSize]
  );

  const handleChainPlace = useCallback(
    (chainLines: readonly DrawingLine[]) => {
      setHistory((current) => {
        const sourceSize = {
          width: current.present.depiction.coordinateSet.width,
          height: current.present.depiction.coordinateSet.height
        };
        const documentChainLines = chainLines.map((line) => ({
          ...line,
          start: unprojectPointPreservingAspect(line.start, sourceSize, paperSize, !isOversizedImport),
          end: unprojectPointPreservingAspect(line.end, sourceSize, paperSize, !isOversizedImport)
        }));
        const updated = documentChainLines.reduce(
          (drawing, candidate) => appendLineToDocument(drawing, candidate),
          current.present
        );
        return updated === current.present ? current : commitHistory(current, updated);
      });
      setSelection(emptySelection());
    },
    [isOversizedImport, setHistory, setSelection, paperSize]
  );

  // Decorative shapes append to `document.annotations`; coordinates land back in depiction space.
  const handleShapePlace = useCallback(
    (shape: 'ellipse' | 'rect' | 'triangle' | 'star', start: NormalizedPoint, end: NormalizedPoint) => {
      setHistory((current) => {
        const source = {
          width: current.present.depiction.coordinateSet.width,
          height: current.present.depiction.coordinateSet.height
        };
        const toDepiction = (point: NormalizedPoint) => {
          const coordNorm = unprojectPointPreservingAspect(point, source, paperSize, !isOversizedImport);
          return { x: coordNorm.x * source.width, y: coordNorm.y * source.height };
        };
        const a = toDepiction(start);
        const b = toDepiction(end);
        const bounds = {
          x: Math.min(a.x, b.x),
          y: Math.min(a.y, b.y),
          width: Math.abs(a.x - b.x),
          height: Math.abs(a.y - b.y)
        };
        if (bounds.width < 1 && bounds.height < 1) return current;
        return commitHistory(
          current,
          appendAnnotation(current.present, {
            id: globalThis.crypto?.randomUUID?.() ?? `shape-${Date.now()}`,
            kind: 'shape',
            shape,
            style: applicableStyle({ kind: 'shape', shape } as Annotation, annotationStyleDefaults),
            bounds
          })
        );
      });
      setSelection(emptySelection());
    },
    [annotationStyleDefaults, isOversizedImport, setHistory, setSelection, paperSize]
  );

  const handleShapeChange = useCallback(
    (shape: PaperShape) => {
      setHistory((current) => {
        const source = current.present.depiction.coordinateSet;
        const points = shape.points.map((point) => {
          const p = unprojectPointPreservingAspect(point, source, paperSize, !isOversizedImport, false);
          return { x: p.x * source.width, y: p.y * source.height };
        });
        const existing = current.present.annotations.find((a) => a.id === shape.id);
        const xs = points.map((p) => p.x),
          ys = points.map((p) => p.y);
        const next = {
          ...(existing?.kind === 'shape'
            ? existing
            : { style: applicableStyle({ kind: 'shape', shape: shape.shape } as Annotation, annotationStyleDefaults) }),
          id: shape.id,
          kind: 'shape' as const,
          shape: shape.shape,
          ...(shape.callout
            ? {
                callout: {
                  ...shape.callout,
                  tip: (() => {
                    const p = unprojectPointPreservingAspect(
                      shape.callout.tip,
                      source,
                      paperSize,
                      !isOversizedImport,
                      false
                    );
                    const tip = { x: roundCoordinate(p.x * source.width), y: roundCoordinate(p.y * source.height) };
                    return existing?.kind === 'shape' &&
                      existing.callout &&
                      Math.hypot(tip.x - existing.callout.tip.x, tip.y - existing.callout.tip.y) < 1e-6
                      ? existing.callout.tip
                      : tip;
                  })()
                }
              }
            : {}),
          ...(shape.rotation !== undefined ? { rotation: shape.rotation } : {}),
          ...(shape.shape === 'path'
            ? {
                points: points as [NormalizedPoint, NormalizedPoint, ...NormalizedPoint[]],
                ...(shape.closed !== undefined ? { closed: shape.closed } : {}),
                ...(shape.pathControls
                  ? {
                      pathControls: mapPathControls(shape.pathControls, (point) => {
                        const p = unprojectPointPreservingAspect(point, source, paperSize, !isOversizedImport, false);
                        return { x: p.x * source.width, y: p.y * source.height };
                      })
                    }
                  : {})
              }
            : {
                bounds: {
                  x: Math.min(...xs),
                  y: Math.min(...ys),
                  width: Math.max(...xs) - Math.min(...xs),
                  height: Math.max(...ys) - Math.min(...ys)
                }
              })
        };
        return commitHistory(current, {
          ...current.present,
          annotations: existing
            ? current.present.annotations.map((a) => (a.id === next.id ? next : a))
            : [...current.present.annotations, next]
        });
      });
      setSelection({ atomIds: [], bondIds: [], annotationIds: [shape.id] });
      if (shape.callout || shape.shape === 'path') setTool('select');
    },
    [annotationStyleDefaults, isOversizedImport, paperSize, setHistory, setSelection, setTool]
  );

  // One history entry per completed creation or handle edit. Points arrive in surface space.
  const handleArrowChange = useCallback(
    (arrow: PaperArrow) => {
      setHistory((current) => {
        const source = current.present.depiction.coordinateSet;
        const convert = (point: NormalizedPoint) => {
          const normalized = unprojectPointPreservingAspect(point, source, paperSize, !isOversizedImport, false);
          return { x: normalized.x * source.width, y: normalized.y * source.height };
        };
        const existing = current.present.annotations.find((candidate) => candidate.id === arrow.id);
        // ADR 0014 -- one reaction arrow per drawing (the rail disables the tools; this is the
        // backstop for a gesture already under way).
        if (
          !existing &&
          arrow.kind === 'reaction-arrow' &&
          current.present.annotations.some((candidate) => candidate.kind === 'reaction-arrow')
        )
          return current;
        const { style: suppliedStyle, ...geometry } = arrow;
        const style = existing
          ? 'style' in existing
            ? existing.style
            : undefined
          : (suppliedStyle ?? annotationStyleDefaults);
        const next = {
          ...geometry,
          ...(style ? { style } : {}),
          from: anchorToDepictionSpace(arrow.from, source, paperSize, !isOversizedImport),
          to: anchorToDepictionSpace(arrow.to, source, paperSize, !isOversizedImport),
          ...(arrow.kind === 'curved-arrow' && arrow.controls
            ? { controls: arrow.controls.map(convert) as [NormalizedPoint, NormalizedPoint] }
            : {})
        };
        if (existing && JSON.stringify(existing) === JSON.stringify(next)) return current;
        const updated = existing
          ? {
              ...current.present,
              annotations: current.present.annotations.map((candidate) =>
                candidate.id === arrow.id ? next : candidate
              )
            }
          : appendAnnotation(current.present, next);
        return commitHistory(current, updated);
      });
      setSelection({ atomIds: [], bondIds: [], annotationIds: [arrow.id] });
    },
    [annotationStyleDefaults, isOversizedImport, paperSize, setHistory, setSelection]
  );

  // #756 -- one committed typing burst / formatting step of a standalone text annotation, as one
  // history entry. A new id lands at `position` (paper-normalized top-left, back in depiction
  // space); an existing one keeps its position. Emptied text removes the annotation.
  const handleTextCommit = useCallback(
    (id: string, position: NormalizedPoint, paragraphs: readonly TextParagraph[]) => {
      setHistory((current) => {
        const annotations = current.present.annotations;
        const existing = annotations.find((annotation) => annotation.id === id);
        if (existing?.kind === 'shape' && existing.callout) {
          const centered = paragraphs.map((p) => ({ ...p, align: 'center' as const }));
          const content = (centered.length ? centered : [{ runs: [] }]) as NonNullable<
            typeof existing.callout
          >['paragraphs'];
          if (JSON.stringify(content) === JSON.stringify(existing.callout.paragraphs)) return current;
          return commitHistory(current, {
            ...current.present,
            annotations: annotations.map((a) =>
              a.id === id ? { ...existing, callout: { ...existing.callout!, paragraphs: content } } : a
            )
          });
        }
        if (isEmptyText(paragraphs)) {
          if (!existing) return current;
          return commitHistory(current, {
            ...current.present,
            annotations: annotations.filter((annotation) => annotation !== existing)
          });
        }
        const [first, ...rest] = paragraphs;
        if (existing?.kind === 'text') {
          if (JSON.stringify(existing.paragraphs) === JSON.stringify(paragraphs)) return current;
          return commitHistory(current, {
            ...current.present,
            annotations: annotations.map((annotation) =>
              annotation === existing ? { ...existing, paragraphs: [first!, ...rest] } : annotation
            )
          });
        }
        if (existing) return current;
        const source = current.present.depiction.coordinateSet;
        const at = unprojectPointPreservingAspect(position, source, paperSize, !isOversizedImport);
        return commitHistory(
          current,
          appendAnnotation(current.present, {
            id,
            kind: 'text',
            position: { x: at.x * source.width, y: at.y * source.height },
            paragraphs: [first!, ...rest]
          })
        );
      });
    },
    [isOversizedImport, paperSize, setHistory]
  );

  return {
    handleLinesChange,
    handleRingPlace,
    handleChainPlace,
    handleShapePlace,
    handleShapeChange,
    handleArrowChange,
    handleTextCommit
  };
}
